// The subagent designer (/subagents/new, /subagents/:t/design): the
// operator describes a specialist in words and Lattice drafts it — name,
// brief, when to use it, a method, what it knows, its data, its tools, who
// may call it — then refines it from further instructions ("give it the
// funding data", "only Julia calls it"). One call to the workspace's own
// model per draft or refinement (backend routes/subagent_designs.py); the
// POST waits ~40 s and a model still thinking answers 202 `running`, which
// `requestDesign` follows by polling the GET every 2 s. Nothing exists in
// the workspace until Save draft / Create & publish (`…/create`); a test
// run (`…/test`) needs the subagent to exist and runs as a hidden one-stage
// workflow run, read through the ordinary run/node/trace routes.
//
// The page works on COMPLETE drafts (every list present): `fullDraft`
// fills what the generated schema leaves optional on the way in, and the
// checks below keep these types assignable to what the server takes. The
// one exception is `connections`: the server sends it (and the inventory's)
// only on a workspace with Connections on, so absent stays absent.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { normalizeGiven, type AutoGiven } from "./subagentConnections";
import type { SubagentBlueprintView } from "./subagentBlueprints";

export type DesignCapabilityKey = "exec" | "web" | "integrations" | "files";

export interface DesignMethod {
  /** Who it is, in a sentence or two. */
  role: string;
  /** What it does, in order. */
  steps: string[];
  /** The bar its work is held to. */
  standards: string[];
  /** What it hands back. */
  output: string;
  /** Things it must never do. */
  never: string[];
}

export interface DesignCapabilities {
  exec: boolean;
  web: boolean;
  integrations: boolean;
  files: "workspace" | "none";
}

export interface DesignConnection {
  /** What is needed ("A database of daily funding rates"). */
  what: string;
  why: string;
}

export interface DesignSimilar {
  template: string;
  name: string;
  why: string;
}

export interface DesignDraft {
  name: string;
  template: string;
  brief: string;
  use_when: string;
  method: DesignMethod;
  /** Markdown composed from `method`: what becomes its instructions. */
  instructions: string;
  skills: string[];
  corpora: string[];
  /** Project ids whose library it reads. */
  projects: string[];
  /** The named person it models ("Ray Dalio"); "" = not a person. */
  speaks_as: string;
  /** The libraries (corpus names, ≤ 5) Lattice builds its profile from. */
  learns_from: string[];
  /** Started from a project with "gather their record": the folder (project id) the person's record is gathered into when it is made. */
  gather_into?: string | null;
  data_sources: string[];
  /** Connection ids it has as its own; absent without Connections (then nothing is sent). */
  connections?: string[] | null;
  suggested_connections: DesignConnection[];
  /** What Lattice added to the model's choice by itself (the connections its brief names, open to every agent, read-only); the card says each with Undo. Absent when nothing. */
  auto_given?: AutoGiven[] | null;
  capabilities: DesignCapabilities;
  capability_reasons: Partial<Record<DesignCapabilityKey, string>>;
  spawnable_by: string[];
  time_limit_minutes: number;
  sample_task: string;
  similar: DesignSimilar[];
  /** What this refinement changed, in words (empty on a first draft). */
  changes: string[];
  notes: string[];
}

export type DesignResponse = { state: "done"; draft_id: string; draft: DesignDraft } | { state: "running"; draft_id: string };

export interface DesignInventory {
  skills: { name: string; description: string }[];
  corpora: { name: string; description: string; documents: number; project_id?: string | null; titles?: string[] }[];
  /** `person`: whose public record was gathered into the folder ("" = none). */
  projects: { id: string; name: string; description: string; corpus: string | null; person?: string }[];
  data_sources: { name: string; title: string; kind: string; summary: string; status: string }[];
  agents: { template: string; name: string }[];
  /** The workspace's connections; absent (or null) without Connections. */
  connections?: DesignInventoryConnection[] | null;
  cap_minutes: number;
  /** A saved subagent's page changes it by targeted edits (…/subagent-blueprints/{t}/edits); absent = no. */
  edit_by_chat?: boolean;
}

export interface DesignInventoryConnection {
  id: string;
  name: string;
  purpose: string;
  status: string;
}

export interface DesignRequest {
  description: string;
  draft_id?: string;
  /** A refinement ("give it the funding data"). */
  instruction?: string;
  /** The draft as the operator has it now (their edits included). */
  current?: DesignDraft;
  /** "From a project": the folder it learns from (a project id), who it speaks as, and whether that person's record is gathered into it. */
  from_project?: string;
  speaks_as?: string;
  gather?: boolean;
}

/** "From a project": what the start screen sends besides the words. */
export interface FromProject {
  project: string;
  speaksAs: string;
  gather: boolean;
}

export interface TestStarted {
  run_id: string;
  node_id: string;
}

type ApiDraft = components["schemas"]["DesignDraft"];
type ApiResponse = components["schemas"]["DesignResponse"];

// What the page sends must be what the server takes (fails to compile otherwise).
const _sendsDraft = (d: DesignDraft): ApiDraft => d;
const _sendsInventory = (i: components["schemas"]["DesignInventory"]): Partial<DesignInventory> => ({ cap_minutes: i.cap_minutes, edit_by_chat: i.edit_by_chat });
void _sendsDraft;
void _sendsInventory;

/** The draft's connections only when the server sent them (api.gen.ts may not name the field yet). */
function connectionsOf(d: ApiDraft): Pick<DesignDraft, "connections"> {
  const c = (d as { connections?: string[] | null }).connections;
  return c ? { connections: [...c] } : {};
}

/** A server draft with every optional part filled (the page edits complete drafts). */
export function fullDraft(d: ApiDraft): DesignDraft {
  const caps = d.capabilities;
  return {
    name: d.name,
    template: d.template,
    brief: d.brief,
    use_when: d.use_when,
    method: {
      role: d.method?.role ?? "",
      steps: d.method?.steps ?? [],
      standards: d.method?.standards ?? [],
      output: d.method?.output ?? "",
      never: d.method?.never ?? [],
    },
    instructions: d.instructions,
    skills: d.skills ?? [],
    corpora: d.corpora ?? [],
    projects: d.projects ?? [],
    speaks_as: d.speaks_as ?? "",
    learns_from: d.learns_from ?? [],
    ...(d.gather_into ? { gather_into: d.gather_into } : {}),
    data_sources: d.data_sources ?? [],
    ...connectionsOf(d),
    suggested_connections: d.suggested_connections ?? [],
    ...(d.auto_given?.length ? { auto_given: d.auto_given.map(normalizeGiven) } : {}),
    capabilities: {
      exec: caps?.exec ?? true,
      web: caps?.web ?? false,
      integrations: caps?.integrations ?? false,
      files: caps?.files === "none" ? "none" : "workspace",
    },
    capability_reasons: d.capability_reasons ?? {},
    spawnable_by: d.spawnable_by ?? [],
    time_limit_minutes: d.time_limit_minutes,
    sample_task: d.sample_task,
    similar: d.similar ?? [],
    changes: d.changes ?? [],
    notes: d.notes ?? [],
  };
}

const POLL_MS = 2_000;
const root = (c: string) => `/api/customers/${encodeURIComponent(c)}/subagent-designs`;
const blueprint = (c: string, t: string) => `/api/customers/${encodeURIComponent(c)}/subagent-blueprints/${encodeURIComponent(t)}`;

export const designKeys = {
  inventory: (c: string | null) => ["subagent-design-inventory", c] as const,
};

const inventoryQuery = (customerId: string) => ({
  queryKey: designKeys.inventory(customerId),
  queryFn: ({ signal }: { signal?: AbortSignal }) => apiFetch<DesignInventory>(`${root(customerId)}/inventory`, { signal }),
  staleTime: 30_000,
});

/** What a design can draw on: skills, libraries, projects, data, the agents that could call it, the time cap. */
export function useDesignInventory(customerId: string | null) {
  return useQuery({ ...inventoryQuery(customerId ?? ""), queryKey: designKeys.inventory(customerId), enabled: !!customerId });
}

/** The inventory now: from the cache while fresh, else read. */
export function fetchDesignInventory(qc: QueryClient, customerId: string): Promise<DesignInventory> {
  return qc.fetchQuery(inventoryQuery(customerId));
}

/**
 * One draft or refinement, followed to its end: the POST, then a GET
 * every 2 s while the model is still at it. `onRunning` is told the
 * draft id as soon as the server has one (a refinement keeps it).
 */
export async function requestDesign(
  customerId: string,
  body: DesignRequest,
  { signal, isCurrent = () => true }: { signal?: AbortSignal; isCurrent?: () => boolean } = {},
): Promise<{ draft_id: string; draft: DesignDraft } | null> {
  let res = await apiFetch<ApiResponse>(root(customerId), { method: "POST", body, signal });
  while (res.state === "running") {
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (!isCurrent() || signal?.aborted) return null;
    res = await apiFetch<ApiResponse>(`${root(customerId)}/${encodeURIComponent(res.draft_id)}`, { signal });
  }
  if (!res.draft) throw new Error("The design came back empty; try again.");
  return isCurrent() ? { draft_id: res.draft_id, draft: fullDraft(res.draft) } : null;
}

/** A blueprint write moves the list, the roster and the graph: everything that reads them refetches. */
function invalidate(qc: QueryClient, customerId: string, view?: SubagentBlueprintView) {
  if (view) qc.setQueryData(["subagent-blueprint", customerId, view.template], view);
  void qc.invalidateQueries({ queryKey: ["subagent-blueprints", customerId] });
  void qc.invalidateQueries({ queryKey: ["subagent-blueprint", customerId] });
  void qc.invalidateQueries({ queryKey: ["configured-agents"] });
  void qc.invalidateQueries({ queryKey: ["customer-agents"] });
  void qc.invalidateQueries({ queryKey: ["org-chart", customerId] });
  void qc.invalidateQueries({ queryKey: ["subagent-data", customerId] });
}

/** Make the subagent from the design: `publish: false` keeps it a draft nobody can call yet. */
export function useCreateFromDesign() {
  const qc = useQueryClient();
  return useMutation({
    // The designer says why itself, with its Try again.
    meta: { errorHandled: true },
    mutationFn: ({ customerId, draftId, draft, publish }: { customerId: string; draftId: string; draft: DesignDraft; publish: boolean }) =>
      apiFetch<SubagentBlueprintView>(`${root(customerId)}/${encodeURIComponent(draftId)}/create`, { method: "POST", body: { draft, publish } }),
    onSuccess: (view, vars) => {
      invalidate(qc, vars.customerId, view);
      // The sample task goes with it to its page's Test.
      if (vars.draft.sample_task) writeTestTask(vars.customerId, view.template, vars.draft.sample_task);
    },
  });
}

/** A draft subagent goes live: these agents may call it from now on. */
export function usePublishSubagent(errorHandled = false) {
  const qc = useQueryClient();
  return useMutation({
    meta: errorHandled ? { errorHandled: true } : undefined,
    mutationFn: ({ customerId, template, spawnableBy }: { customerId: string; template: string; spawnableBy: string[] }) =>
      apiFetch<SubagentBlueprintView>(`${blueprint(customerId, template)}/publish`, { method: "POST", body: { spawnable_by: spawnableBy } }),
    onSuccess: (view, vars) => invalidate(qc, vars.customerId, view),
  });
}

const testTaskKey = (customerId: string, template: string) => `corvus:test-task:${customerId}:${template}`;

/**
 * A draft's test task, in this browser per subagent: what the designer proposed
 * or what was typed in Test (the block has no field for it: its schema is in
 * the iPhone contract), so the box is filled on its page later.
 */
export function readTestTask(customerId: string, template: string): string {
  try {
    return window.localStorage.getItem(testTaskKey(customerId, template)) ?? "";
  } catch {
    return "";
  }
}

export function writeTestTask(customerId: string, template: string, task: string) {
  try {
    if (task) window.localStorage.setItem(testTaskKey(customerId, template), task);
    else window.localStorage.removeItem(testTaskKey(customerId, template));
  } catch {
    // this visit only
  }
}

/** One test task, run now as a hidden run; its stage is read like any workflow run's. */
export function useTestSubagent() {
  return useMutation({
    // The Test panel says why itself.
    meta: { errorHandled: true },
    mutationFn: ({ customerId, template, task }: { customerId: string; template: string; task: string }) =>
      apiFetch<TestStarted>(`${blueprint(customerId, template)}/test`, { method: "POST", body: { task } }),
  });
}

export type TestSummary = components["schemas"]["TestRunSummary"];

/** Its latest tests (no list of runs shows one), read again while one of them runs. */
export function useSubagentTests(customerId: string | null, template: string | null) {
  return useQuery({
    queryKey: ["subagent-tests", customerId, template],
    enabled: !!customerId && !!template,
    queryFn: ({ signal }) => apiFetch<TestSummary[]>(`${blueprint(customerId ?? "", template ?? "")}/tests`, { signal }),
    staleTime: 5_000,
    refetchInterval: (q) => (q.state.data?.some((t) => !ENDED.has(t.status)) ? 5_000 : false),
  });
}

const ENDED = new Set(["completed", "partial", "failed", "timed_out", "cancelled", "skipped"]);

/** A subagent saved from the designer but not published yet (the server's `subagent.draft`). */
export function isDraftSubagent(view: Pick<SubagentBlueprintView, "subagent">): boolean {
  return (view.subagent as { draft?: boolean | null }).draft === true;
}
